<template>
	<div>
		<transition name="expand">
			<amend-balance-modal v-if="showAmendBalanceModal" :account="editAccount" :tenant="editAccount.tenant" />
		</transition>
		<transition name="expand">
			<balance-history-modal v-if="showBalanceHistoryModal" :account="editAccount" />
		</transition>
		<transition name="expand">
			<barred-cli-modal v-if="showBlockedListModal" :account="editAccount" @save="saveBarredCliList" />
		</transition>
		<div class="stage">
			<div class="row margin-b20">
				<div class="col-xs-12 col-sm-6 col-md-6 col-lg-6">
					<div class="backBtn mobileMargin-b15">
						<router-link to="/accounts"
							><p
								class="textUppercase textGrey textXsml fontHeavy hover:text-[#555555] dark:hover:text-[#eeeeee]"
							>
								<span class="fa-light fa-arrow-left textGrey"></span> Take me back
							</p></router-link
						>
					</div>
				</div>
				<div class="col-xs-12 col-sm-6 col-md-6 col-lg-6">
					<ul class="stepBtnsRight">
						<li>
							<button
								:disabled="!editAccount.name || !editAccount.tenant"
								class="jmiBtn btnLg green"
								@click="save()"
							>
								{{ editAccount.id ? 'Save' : 'Create' }}
							</button>
						</li>
						<li><button class="jmiBtn btnLg red" @click="routeTo('/accounts')">Close</button></li>
					</ul>
				</div>
			</div>
			<div class="row margin-b10">
				<div class="col-xs-12 col-sm-12 col-md-12 col-lg-8 col-lg-offset-2">
					<div class="row margin-b10">
						<div class="col-xs-12 col-sm-12 col-md-4 col-lg-4">
							<!-- <p class="textSml textGrey fontHeavy textUppercase">User</p> -->
						</div>
					</div>
					<div class="row margin-b20">
						<div class="col-xs-12 col-sm-12 col-md-12 col-lg-12">
							<div class="card detailCard flex flex-col rounded-sm shadow-lg">
								<div class="mx-auto w-full">
									<div class="col-sm-12 textCenter">
										<div class="navAvatar margin-b10 flex w-full justify-center">
											<img src="/assets/images/teleflow-logo-black.png" width="200" />
										</div>
									</div>
								</div>
								<div v-if="!loading">
									<div v-if="editAccount.id" class="margin-b10 my-4 text-center">
										<span>Current Balance: </span
										><strong>{{ $global.currencyGbp(editAccount.balanceGbp, 2) }}</strong>
									</div>
									<template v-if="editAccount.id">
										<div
											v-if="editAccount.status !== 'active'"
											class="mb-4 rounded-sm bg-red-100 p-4 text-center text-red-700"
										>
											<span class="fa-light fa-triangle-exclamation mr-2"></span>
											This account is currently set to {{ editAccount.status }}. Please set to
											active to enable calls.
										</div>
										<div
											v-if="editAccount.billingType === 'PRE_PAID' && editAccount.balanceGbp <= 0"
											class="mb-4 rounded-sm bg-red-100 p-4 text-center text-red-700"
										>
											<span class="fa-light fa-triangle-exclamation mr-2"></span>
											This account has zero balance and is pre-paid. Please top up to enable
											calls.
										</div>
										<div
											v-if="!editAccount.aLegTariffId"
											class="mb-4 rounded-sm bg-red-100 p-4 text-center text-red-700"
										>
											<span class="fa-light fa-triangle-exclamation mr-2"></span>
											This account has no active tariff. Please assign a tariff to enable calls.
										</div>
										<div
											v-if="!editAccount.bLegTariffProfileId"
											class="mb-4 rounded-sm bg-red-100 p-4 text-center text-red-700"
										>
											<span class="fa-light fa-triangle-exclamation mr-2"></span>
											This account has no active routing profile. Please assign a routing profile
											to enable calls.
										</div>
									</template>
									<div
										class="flex w-full flex-col flex-wrap items-center justify-center gap-4 xs:flex-row"
									>
										<div v-if="editAccount.id" class="w-full sm:w-auto">
											<button
												class="jmiBtn blue withIcon w-full sm:w-auto"
												@click="showAmendBalance()"
											>
												<span class="fa-light fa-money-bill-transfer"></span>Adjust Balance
											</button>
										</div>
										<div v-if="editAccount.id" class="w-full sm:w-auto">
											<button
												class="jmiBtn grey withIcon w-full sm:w-auto"
												@click="showBalanceHistory()"
											>
												<span class="fa-light fa-clipboard-user"></span>Balance History
											</button>
										</div>
										<div v-if="editAccount.id" class="w-full sm:w-auto">
											<button
												class="jmiBtn red withIcon w-full sm:w-auto"
												@click="showBlockedList()"
											>
												<span class="fa-light fa-phone-slash"></span>Blocked CLI List
											</button>
										</div>
									</div>
									<div class="mb-6 mt-4 border-b border-dark dark:border-light">
										<h3 class="mb-2">Personal Information</h3>
									</div>
									<div class="mx-auto mb-2 flex w-full flex-row flex-wrap md:mb-5">
										<div class="w-1/2 px-2">
											<div class="formGroup">
												<label class="textGrey">Company Name</label>
												<FormKit
													v-model="editAccount.name"
													type="text"
													class="form-control"
													placeholder="Name"
													name="Company Name"
													:validation="'required'"
													validation-visibility="live"
												/>
											</div>
										</div>
										<div class="w-1/2 px-2">
											<div class="formGroup withIcon">
												<label class="textGrey">Account Status</label>
												<select v-model="editAccount.status">
													<option value="active">Active</option>
													<option value="inactive">Inactive</option>
													<option value="suspended">Suspended</option>
												</select>
												<span class="fa-light fa-chevron-down"></span>
											</div>
										</div>
									</div>
									<div
										v-if="
											!editAccount.id &&
											(tenants?.length > 1 || $global.hasUserRole(userRoles, 'reseller'))
										"
										class="mb-2 w-full px-2 md:mb-5"
									>
										<div class="formGroup withIcon">
											<label class="textGrey">Tenant</label>
											<select v-model="editAccount.tenant" @change="reloadTariffs">
												<option disabled :value="null">-- Select Tenant --</option>
												<option v-for="t in tenants" :key="t.id" :value="t">
													{{ t.companyName }}
												</option>
											</select>
											<span class="fa-light fa-chevron-down"></span>
										</div>
									</div>
									<div class="mb-6 mt-4 border-b border-dark dark:border-light">
										<h3 class="mb-2">Billing</h3>
									</div>
									<div class="mx-auto mb-2 flex w-full flex-row flex-wrap md:mb-5">
										<div class="mb-2 w-full px-2 md:mb-5">
											<div class="formGroup withIcon">
												<label class="textGrey">Billing Type</label>
												<select v-model="editAccount.billingType">
													<option disabled :value="null">-- Select Auth Type --</option>
													<option value="PRE_PAID">Pre-paid</option>
													<option value="POST_PAID">Post-paid</option>
												</select>
												<span class="fa-light fa-chevron-down"></span>
											</div>
										</div>
										<div class="w-full px-2 md:w-1/2">
											<div class="formGroup">
												<label class="textGrey">Balance Alert Amount</label>
												<input
													v-model="editAccount.alertAmount"
													type="number"
													class="form-control"
													placeholder="0"
												/>
											</div>
										</div>
										<div class="w-full px-2 md:w-1/2">
											<div class="formGroup">
												<label class="textGrey">Balance Alert Email</label>
												<input
													v-model="editAccount.alertEmail"
													type="email"
													class="form-control"
													placeholder="Alert Email"
												/>
											</div>
										</div>
									</div>
									<div class="mb-6 mt-4 border-b border-dark dark:border-light">
										<h3 class="mb-2">Authorisation</h3>
									</div>
									<div class="mb-2 w-full px-2 md:mb-5">
										<div class="formGroup withIcon">
											<label class="textGrey">Auth Type</label>
											<select v-model="editAccount.authType">
												<option :value="null">Not required (Inbound only)</option>
												<option value="SOURCE_IP">Source IP(s)</option>
												<option value="SIP_HEADER">Custom SIP Header</option>
											</select>
											<span class="fa-light fa-chevron-down"></span>
										</div>
									</div>
									<div v-if="editAccount.authType === 'SIP_HEADER'" class="mb-2 px-2 md:mb-5">
										<div class="formGroup">
											<label class="textGrey">Auth Details</label>
											<input
												v-model="editAccount.authParameters"
												type="text"
												class="form-control"
												placeholder="Enter SIP Header value"
											/>
										</div>
									</div>
									<div v-if="editAccount.authType === 'SOURCE_IP'" class="mb-2 w-full px-2 md:mb-5">
										<div class="formGroup">
											<label class="textGrey">Auth Details</label>
										</div>
										<div class="mb-3 mt-2">
											<button class="fab fabSm green" @click="addIP()">
												<span class="fa-light fa-plus"></span>
											</button>
										</div>
										<div v-for="(ip, i) in ipAddresses" :key="i" class="mb-3 w-full">
											<div class="flex w-full flex-row">
												<div class="w-full md:w-1/2">
													<div class="formGroup">
														<input
															v-model="ipAddresses[i]"
															type="text"
															class="form-control"
															placeholder="Enter IP Address"
														/>
													</div>
												</div>
												<div v-if="ipAddresses.length > 1" class="px-2">
													<button class="fab fabSm red" @click="removeIP(i)">
														<span class="fa-light fa-minus"></span>
													</button>
												</div>
											</div>
										</div>
									</div>
									<div class="mb-6 mt-4 border-b border-dark dark:border-light">
										<h3 class="mb-2">Routing</h3>
									</div>
									<div class="mx-auto mb-2 flex w-full flex-row flex-wrap md:mb-5">
										<div
											class="px-2"
											:class="
												$global.hasUserRole(userRoles, 'accounts:setnetworknumber')
													? 'w-1/2'
													: 'w-full'
											"
										>
											<div class="formGroup">
												<label class="textGrey">Prefix</label>
												<div class="flex gap-1">
													<FormKit
														v-model="editAccount.prefix"
														type="text"
														class="form-control"
														placeholder="Prefix"
														name="Prefix"
													/>
													<button
														class="jmiBtn blue"
														:disabled="!editAccount.tenant || suggestNextLoading"
														@click="suggestNextPrefix"
													>
														<span class="hidden sm:block">Suggest</span>
														<span class="fa-solid fa-arrow-up-1-9 sm:hidden"></span>
													</button>
												</div>
											</div>
										</div>
										<div
											v-if="$global.hasUserRole(userRoles, 'accounts:setnetworknumber')"
											class="w-1/2 px-2"
										>
											<div class="formGroup">
												<label class="textGrey">Default Network Number</label>
												<FormKit
													v-model="editAccount.defaultNetworkNumber"
													type="text"
													class="form-control"
													placeholder="Default Network Number"
													name="Default Network Number"
												/>
											</div>
										</div>
									</div>
									<div
										v-if="editAccount.tenant"
										class="mx-auto mb-2 flex w-full flex-row flex-wrap md:mb-5"
									>
										<div class="w-1/2 px-2">
											<div class="formGroup withIcon">
												<a
													class="group flex cursor-pointer gap-1"
													:href="
														editAccount.aLegTariffId || !editAccount.aLegTariffId === null
															? `/tariffs/${editAccount.aLegTariffId}`
															: 'javascript:void(0);'
													"
													:target="
														editAccount.aLegTariffId || !editAccount.aLegTariffId === null
															? `_blank`
															: ''
													"
												>
													<label class="textGrey cursor-pointer group-hover:underline"
														>A-Leg Tariff</label
													>
													<svg
														v-if="
															editAccount.aLegTariffId ||
															!editAccount.aLegTariffId === null
														"
														class="mt-1"
														width="14"
														height="14"
														viewBox="0 0 24 24"
														fill="none"
														xmlns="http://www.w3.org/2000/svg"
													>
														<path
															d="M20 5H8V9H6V3H22V21H6V15H8V19H20V5Z"
															fill="currentColor"
														/>
														<path
															d="M13.0743 16.9498L11.6601 15.5356L14.1957 13H2V11H14.1956L11.6601 8.46451L13.0743 7.05029L18.024 12L13.0743 16.9498Z"
															fill="currentColor"
														/>
													</svg>
												</a>
												<select v-model="editAccount.aLegTariffId">
													<option :value="null">-- Select Tariff --</option>
													<option
														v-for="tariff in tenantTariffs"
														:key="tariff.id"
														:value="tariff.id"
													>
														{{ tariff.name }}
													</option>
												</select>
												<span class="fa-light fa-chevron-down"></span>
											</div>
										</div>
										<div class="w-1/2 px-2">
											<div class="formGroup withIcon">
												<a
													class="group flex cursor-pointer gap-1"
													:href="
														editAccount.bLegTariffProfileId ||
														!editAccount.bLegTariffProfileId === null
															? `/tariffprofiles/${editAccount.bLegTariffProfileId}`
															: 'javascript:void(0);'
													"
													:target="
														editAccount.bLegTariffProfileId ||
														!editAccount.bLegTariffProfileId === null
															? `_blank`
															: ''
													"
												>
													<label class="textGrey cursor-pointer group-hover:underline"
														>B-Leg Routing Profile</label
													>
													<svg
														v-if="
															editAccount.bLegTariffProfileId ||
															!editAccount.bLegTariffProfileId === null
														"
														class="mt-1"
														width="14"
														height="14"
														viewBox="0 0 24 24"
														fill="none"
														xmlns="http://www.w3.org/2000/svg"
													>
														<path
															d="M20 5H8V9H6V3H22V21H6V15H8V19H20V5Z"
															fill="currentColor"
														/>
														<path
															d="M13.0743 16.9498L11.6601 15.5356L14.1957 13H2V11H14.1956L11.6601 8.46451L13.0743 7.05029L18.024 12L13.0743 16.9498Z"
															fill="currentColor"
														/>
													</svg>
												</a>
												<select v-model="editAccount.bLegTariffProfileId">
													<option :value="null">-- Select Routing Profile --</option>
													<option
														v-for="tariffProfile in tenantTariffProfiles"
														:key="tariffProfile.id"
														:value="tariffProfile.id"
													>
														{{ tariffProfile.name }}
													</option>
												</select>
												<span class="fa-light fa-chevron-down"></span>
											</div>
										</div>
									</div>
									<div class="mb-6 mt-4 border-b border-dark dark:border-light">
										<h3 class="mb-2">Caller ID</h3>
									</div>
									<div class="mx-auto mb-2 flex w-full flex-row flex-wrap md:mb-5">
										<div class="w-1/2 px-2">
											<div class="formGroup withIcon">
												<label class="textGrey">Caller ID Policy</label>
												<select v-model="editAccount.cliPolicy" class="form-control">
													<option :value="null">No policy (passthrough)</option>
													<option value="OVERRIDE_FORCED">CLI override (forced)</option>
													<option value="OVERRIDE_IF_INVALID">
														CLI override (if invalid)
													</option>
													<option value="OVERRIDE_INTERNATIONAL">
														CLI override (international)
													</option>
													<option value="OVERRIDE_INTERNATION_OR_INVALID">
														CLI override (international or invalid)
													</option>
												</select>
												<span class="fa-light fa-chevron-down"></span>
											</div>
										</div>
										<div v-if="editAccount.cliPolicy" class="w-1/2 px-2">
											<div class="formGroup">
												<label class="textGrey">Caller ID Override</label>
												<FormKit
													v-model="editAccount.cliOverride"
													type="text"
													class="form-control"
													placeholder="CLI Override"
													name="CLI Override"
												/>
											</div>
										</div>
									</div>
									<div class="mb-6 mt-4 border-b border-dark dark:border-light">
										<h3 class="mb-2">Fraud Control</h3>
									</div>
									<div class="mx-auto mb-2 flex w-full flex-row flex-wrap md:mb-5">
										<div class="w-full px-2 md:w-1/2">
											<div class="formGroup">
												<label class="textGrey">Max Call Duration (mins)</label>
												<input
													v-model="editAccount.maxCallDurationMins"
													type="number"
													class="form-control"
													placeholder="Max Call Duration"
												/>
											</div>
										</div>
										<div class="w-full px-2 md:w-1/2">
											<div class="formGroup">
												<label class="textGrey">Concurrent call limit</label>
												<input
													v-model="editAccount.concurrentCallLimit"
													type="number"
													class="form-control"
													placeholder="Concurrent call limit"
												/>
											</div>
										</div>
									</div>
									<div class="mx-auto mb-2 flex w-full flex-row flex-wrap md:mb-5">
										<div class="w-1/2 px-2">
											<div class="formGroup">
												<label class="textGrey">Max Daily Spend (GBP)</label>
												<input
													v-model="editAccount.limitDaily"
													type="number"
													class="form-control"
													step="0.01"
												/>
											</div>
										</div>
										<div class="w-1/2 px-2">
											<div class="formGroup">
												<label class="textGrey">Max Monthly Spend (GBP)</label>
												<input
													v-model="editAccount.limitMonthly"
													type="number"
													class="form-control"
													step="0.01"
												/>
											</div>
										</div>
									</div>
									<div class="mb-6 mt-4 border-b border-dark dark:border-light">
										<h3 class="mb-2">Other</h3>
									</div>
									<div class="mb-2 w-full px-2 md:mb-5">
										<div class="formGroup">
											<label class="textGrey">Reseller</label>
											<input
												v-model="editAccount.reseller"
												type="text"
												class="form-control"
												placeholder="Reseller"
											/>
										</div>
									</div>
									<div class="mb-6 mt-4 border-b border-dark dark:border-light">
										<h3 class="mb-2">Contact Information</h3>
									</div>
									<div class="mx-auto mb-2 flex w-full flex-col flex-wrap md:mb-5 md:flex-row">
										<div class="w-full px-2 md:w-1/2">
											<div class="formGroup">
												<label class="textGrey">Contact Name</label>
												<input
													v-model="editAccount.contactName"
													type="text"
													class="form-control"
													placeholder="Contact Name"
												/>
											</div>
										</div>
										<div class="w-full px-2 md:w-1/2">
											<div class="formGroup">
												<label class="textGrey">Contact Email</label>
												<input
													v-model="editAccount.email"
													type="text"
													class="form-control"
													placeholder="Contact Email"
												/>
											</div>
										</div>
									</div>
									<div class="mx-auto mb-2 flex w-full flex-row flex-wrap md:mb-5">
										<div class="w-1/2 px-2">
											<div class="formGroup">
												<label class="textGrey">Contact Phone</label>
												<input
													v-model="editAccount.landline"
													type="text"
													class="form-control"
													placeholder="Contact Phone"
												/>
											</div>
										</div>
										<div class="w-1/2 px-2">
											<div class="formGroup">
												<label class="textGrey">Contact Mobile</label>
												<input
													v-model="editAccount.mobile"
													type="text"
													class="form-control"
													placeholder="Contact Mobile"
												/>
											</div>
										</div>
									</div>
									<div class="mx-auto mb-2 flex w-full flex-row flex-wrap md:mb-5">
										<div class="w-1/2 px-2">
											<div class="formGroup">
												<label class="textGrey">Address Line 1</label>
												<input
													v-model="editAccount.address1"
													type="text"
													class="form-control"
													placeholder="Address Line 1"
												/>
											</div>
										</div>
									</div>
									<div class="mx-auto mb-2 flex w-full flex-row flex-wrap md:mb-5">
										<div class="w-1/2 px-2">
											<div class="formGroup">
												<label class="textGrey">Address Line 2</label>
												<input
													v-model="editAccount.address2"
													type="text"
													class="form-control"
													placeholder="Address Line 2"
												/>
											</div>
										</div>
										<div class="w-1/2 px-2">
											<div class="formGroup">
												<label class="textGrey">City</label>
												<input
													v-model="editAccount.city"
													type="text"
													class="form-control"
													placeholder="City"
												/>
											</div>
										</div>
									</div>
									<div class="mx-auto mb-2 flex w-full flex-row flex-wrap md:mb-5">
										<div class="w-1/2 px-2">
											<div class="formGroup">
												<label class="textGrey">County</label>
												<input
													v-model="editAccount.county"
													type="text"
													class="form-control"
													placeholder="County"
												/>
											</div>
										</div>
										<div class="w-1/2 px-2">
											<div class="formGroup">
												<label class="textGrey">Postcode</label>
												<input
													v-model="editAccount.postcode"
													type="text"
													class="form-control"
													placeholder="Postcode"
												/>
											</div>
										</div>
									</div>
									<div class="margin-b20 mt-8 flex px-2">
										<div class="col-md-12">
											<ul class="stepBtns">
												<li>
													<button
														:disabled="!editAccount.name || !editAccount.tenant"
														class="jmiBtn btnLg green"
														@click="save()"
													>
														{{ editAccount.id ? 'Save' : 'Create' }}
													</button>
												</li>
												<li>
													<button class="jmiBtn btnLg red" @click="routeTo('/accounts')">
														Close
													</button>
												</li>
											</ul>
										</div>
									</div>
								</div>
								<div v-else>
									<div class="row margin-b20 center-xs center-md center-lg">
										<div class="loader"></div>
									</div>
								</div>
							</div>
						</div>
					</div>
				</div>
			</div>
		</div>
	</div>
</template>

<script>
import accountsApi from '../scripts/accounts/accounts.api.js'
import tariffProfilesApi from '../scripts/tariffprofiles/tariffprofiles.api'
import tariffsApi from '../scripts/tariffs/tariffs.api'
import { router } from '../router'
import { computed } from 'vue'

export default {
	inject: ['user', 'userRoles', 'tenants'],
	provide() {
		return {
			editAccount: computed({
				get: () => this.editAccount,
				set: (val) => (this.editAccount = val)
			}),
			editTenant: computed(() => this.editAccount.tenant),
			showAmendBalanceModal: computed({
				get: () => this.showAmendBalanceModal,
				set: (val) => (this.showAmendBalanceModal = val)
			}),
			showBalanceHistoryModal: computed({
				get: () => this.showBalanceHistoryModal,
				set: (val) => (this.showBalanceHistoryModal = val)
			}),
			showBlockedListModal: computed({
				get: () => this.showBlockedListModal,
				set: (val) => (this.showBlockedListModal = val)
			}),
			showBarredCliEditModal: computed({
				get: () => false,
				set: () => {}
			})
		}
	},
	data() {
		return {
			editAccount: {
				name: '',
				tenant: null,
				prefix: '',
				aLegTariffId: null,
				bLegTariffProfileId: null,
				authType: null,
				authParameters: null,
				billingType: 'PRE_PAID',
				limitDaily: 0,
				limitMonthly: 0,
				maxCallDurationMins: 240,
				concurrentCallLimit: 0,
				reseller: '',
				contactName: '',
				email: '',
				landline: '',
				mobile: '',
				address1: '',
				address2: '',
				city: '',
				county: '',
				postcode: '',
				status: 'active',
				alwaysTranscode: false,
				alertAmount: 0,
				alertEmail: '',
				defaultNetworkNumber: null,
				cliPolicy: null,
				cliOverride: null
			},
			ipAddresses: [''],
			tariffs: [],
			tariffProfiles: [],
			showAmendBalanceModal: false,
			showBalanceHistoryModal: false,
			showBlockedListModal: false,
			selectedTariffProfile: null,
			loading: true,
			suggestNextLoading: false
		}
	},

	computed: {
		tenantTariffProfiles() {
			return this.tariffProfiles
				.filter((tp) => tp.tenant && tp.tenant.id === this.editAccount.tenant.id)
				.sort((a, b) => a.name.localeCompare(b.name))
		},
		tenantTariffs() {
			return this.tariffs
				.filter((t) => t.tenant && t.tenant.id === this.editAccount.tenant.id)
				.sort((a, b) => a.name.localeCompare(b.name))
		}
	},

	async created() {
		try {
			const tariffProfilesResult = tariffProfilesApi.getAll()

			if (this.$route.params.id !== 'new') {
				const result = await accountsApi.getById(this.$route.params.id)
				this.editAccount = result.data

				if (this.editAccount.authType === 'SOURCE_IP' && this.editAccount.authParameters)
					this.ipAddresses = this.editAccount.authParameters
			} else if (this.tenants?.length === 1) this.editAccount.tenant = this.tenants[0]

			await this.reloadTariffs()

			this.tariffProfiles = (await tariffProfilesResult).data.results

			this.loading = false
		} catch (e) {
			this.$swal({
				title: 'Error loading account',
				icon: 'error',
				confirmButtonText: 'Confirm',
				text: 'Unknown error occurred, please try again. If the issue persists, please raise a support ticket.'
			}).then(() => {
				router.push('/accounts')
			})
			console.error(e)
		}
	},

	methods: {
		saveBarredCliList(list) {
			accountsApi.saveBarredCliList(this.editAccount.id, { barredCliList: list })
			this.editAccount.barredCliList = list
			this.closeBlockedList()
		},
		async reloadTariffs() {
			if (this.editAccount.tenant?.id) {
				const tariffApiResult = tariffsApi.getAll({
					where: {
						selectedTenant: this.editAccount.tenant?.id
					}
				})

				this.tariffs = (await tariffApiResult).data.results
			}
		},
		removeIP(i) {
			this.ipAddresses.splice(i, 1)
		},
		addIP() {
			this.ipAddresses.push('')
		},
		routeTo(routeName) {
			router.push(routeName)
		},
		showAmendBalance() {
			this.showAmendBalanceModal = true
		},
		showBalanceHistory() {
			this.showBalanceHistoryModal = true
		},
		showBlockedList() {
			this.showBlockedListModal = true
		},
		closeBlockedList() {
			this.showBlockedListModal = false
		},
		closeAmendBalance() {
			this.showAmendBalanceModal = false
		},
		closeBalanceHistory() {
			this.showBalanceHistoryModal = false
		},
		save() {
			if (this.editAccount.authType === 'SOURCE_IP') {
				if (
					this.ipAddresses.some(
						(ip) =>
							!ip
								.trim()
								.match(/^(25[0-5]|2[0-4]\d|1\d{2}|[1-9]?\d)(\.(25[0-5]|2[0-4]\d|1\d{2}|[1-9]?\d)){3}$/)
					)
				) {
					this.$swal({
						title: 'Error',
						icon: 'error',
						text: 'Invalid IP addresses found, please check before re-submitting',
						confirmButtonText: 'Try Again'
					})
					return
				}
				this.editAccount.authParameters = this.ipAddresses.map((ip) => ip.trim()).filter((ip) => ip)
			}
			if (!this.editAccount.authType) this.editAccount.authParameters = null

			if (!this.editAccount.id) {
				accountsApi
					.create(this.editAccount)
					.then((result) => {
						if (result && result.error)
							this.$swal({
								title: 'Error',
								icon: 'error',
								text:
									result.error.indexOf('ER_DUP_ENTRY') > -1
										? 'Prefix already exists for this tenant'
										: result.error,
								confirmButtonText: 'Try Again'
							})
						else this.routeTo('/accounts')
					})
					.catch(console.error)
			} else {
				accountsApi
					.update(this.editAccount)
					.then((result) => {
						if (result && result.error)
							this.$swal({
								title: 'Error',
								icon: 'error',
								text:
									result.error.indexOf('ER_DUP_ENTRY') > -1
										? 'Prefix already exists for this tenant'
										: result.error,
								confirmButtonText: 'Try Again'
							})
						else this.routeTo('/accounts')
					})
					.catch(console.error)
			}
		},
		suggestNextPrefix() {
			this.suggestNextLoading = true
			accountsApi
				.suggestNextPrefix(this.editAccount.tenant.id)
				.then((result) => {
					this.editAccount.prefix = result.data
					this.suggestNextLoading = false
				})
				.catch((e) => {
					this.$swal({
						title: 'Error',
						icon: 'error',
						text: 'Error suggesting next prefix, please try again',
						confirmButtonText: 'Try Again'
					})
					console.error(e)
					this.suggestNextLoading = false
				})
		}
	}
}
</script>
